/*  RichTextView — render HTML produced by RichTextEditor.
    ────────────────────────────────────────────────────────
    Content is authored by trusted CoE staff via Tiptap StarterKit +
    Embed node, so it's constrained to a safe node set (bold / italic /
    headings / lists / blockquote) plus explicit embed placeholders.
    Uses `dangerouslySetInnerHTML` intentionally — the alternative
    (`react-markdown` etc.) would drop the HTML shape entirely.

    Embed handling: authored HTML wraps embeds inside
    `<div data-embed-html data-html="B64">…</div>`. Before rendering
    we walk the DOM, decode the `data-html` attribute, and replace the
    placeholder with the original iframe / script / raw HTML so
    learners see the live YouTube / Vimeo / etc.

    Usage:
        <RichTextView html={q.prompt} className="text-sm" />
*/
import React, { useMemo } from "react";


// Detects HTML content that visually has no text. Handles both raw text
// (legacy quiz rows) and HTML from Tiptap (which emits `<p></p>` for
// an empty editor). Returns true when the string is safe to treat as
// empty for validation / conditional-render purposes.
export function htmlIsEmpty(html) {
    if (!html) return true;
    const plain = String(html)
        .replace(/<style[\s\S]*?<\/style>/gi, "")
        .replace(/<script[\s\S]*?<\/script>/gi, "")
        // Strip embed placeholders before deciding emptiness — a lesson
        // that's *only* a YouTube embed should not be considered empty.
        .replace(/<div[^>]*data-embed-html[^>]*>[\s\S]*?<\/div>/gi, " EMBED ")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim();
    return plain.length === 0;
}


function decodeB64Utf8(b64) {
    try {
        return decodeURIComponent(escape(atob(String(b64 || ""))));
    } catch { return ""; }
}


// Convert a raw editor HTML string into learner-facing HTML by:
//   • unwrapping <div data-embed-html data-html="B64"> → decoded HTML
//   • wrapping legacy plain-text rows in a <p> so `.prose` styling
//     stays consistent across old + new content.
export function expandEmbeds(html) {
    if (!html) return "";
    const isPlain = !/<[a-z][\s\S]*>/i.test(String(html));
    if (isPlain) return `<p>${html}</p>`;
    return String(html).replace(
        /<div\b[^>]*\bdata-embed-html\b[^>]*>[\s\S]*?<\/div>/gi,
        (match) => {
            const m = match.match(/data-html="([^"]*)"/i);
            if (!m) return "";
            return decodeB64Utf8(m[1]);
        },
    );
}


export default function RichTextView({ html, className = "" }) {
    const expanded = useMemo(() => expandEmbeds(html), [html]);
    if (htmlIsEmpty(html)) return null;
    return (
        <div
            className={`prose prose-sm max-w-none prose-p:my-0.5 prose-ul:my-1 prose-ol:my-1 prose-headings:mb-1 prose-headings:mt-1 ${className}`}
            dangerouslySetInnerHTML={{ __html: expanded }}
        />
    );
}
